import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  nextResponsibilityCountdown,
  responsibilityConfirmDelaySeconds,
  responsibilityRoles,
  responsibilityStorageKey,
  type ResponsibilityRole,
} from '../model/workspace-responsibility';

const responsibilityCopy: Record<
  ResponsibilityRole,
  { title: string; body: string }
> = {
  teacher: {
    title: '教师责任说明',
    body: '仅在本人负责的班级和课程范围内处理学生、作业、课件、考勤、成绩及请假数据，并妥善保护学生隐私。',
  },
  student: {
    title: '学生责任说明',
    body: '仅使用本人的课程与学习数据；请假由本人提交，家庭邀请仅用于绑定自己的家长。',
  },
  guardian: {
    title: '家长责任说明',
    body: '仅查看已绑定孩子的班级、课程与学习数据，不得代替学生执行作业、提交作业或其他教学操作。',
  },
};

export function WorkspaceResponsibilityGate({
  accountId,
  tenantId,
  roles,
}: {
  accountId: string | undefined;
  tenantId: string | undefined;
  roles: readonly string[];
}) {
  const responsibilityRoleList = useMemo(
    () => responsibilityRoles(roles),
    [roles],
  );
  const pending = useMemo(() => {
    if (!accountId || !tenantId) return [];
    return responsibilityRoleList.filter(
      (role) =>
        window.localStorage.getItem(
          responsibilityStorageKey(accountId, tenantId, role),
        ) !== 'accepted',
    );
  }, [accountId, tenantId, responsibilityRoleList]);

  const gateIdentity = `${accountId ?? ''}:${tenantId ?? ''}:${pending.join(',')}`;
  const [confirmedIdentity, setConfirmedIdentity] = useState<string | null>(
    null,
  );
  const [remainingSeconds, setRemainingSeconds] = useState(
    responsibilityConfirmDelaySeconds,
  );

  useEffect(() => {
    if (pending.length === 0 || confirmedIdentity === gateIdentity) return;
    const timer = window.setInterval(() => {
      setRemainingSeconds((current) => {
        const next = nextResponsibilityCountdown(current);
        if (next === 0) window.clearInterval(timer);
        return next;
      });
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [gateIdentity, pending.length, confirmedIdentity]);

  if (
    !accountId ||
    !tenantId ||
    pending.length === 0 ||
    confirmedIdentity === gateIdentity
  ) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="responsibility-title"
    >
      <section className="w-full max-w-lg rounded-2xl border bg-background p-6 shadow-2xl">
        <p className="text-xs font-semibold tracking-[0.16em] text-brand-600">
          首次进入确认
        </p>
        <h2 className="mt-2 text-xl font-semibold" id="responsibility-title">
          工作区责任说明
        </h2>
        <div className="mt-5 space-y-3">
          {pending.map((role) => (
            <article className="rounded-xl border bg-(--surface-wash-soft) p-4" key={role}>
              <h3 className="font-medium">{responsibilityCopy[role].title}</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {responsibilityCopy[role].body}
              </p>
            </article>
          ))}
        </div>
        <Button
          className="mt-6 w-full"
          disabled={remainingSeconds > 0}
          onClick={() => {
            for (const role of pending) {
              window.localStorage.setItem(
                responsibilityStorageKey(accountId, tenantId, role),
                'accepted',
              );
            }
            setConfirmedIdentity(gateIdentity);
          }}
        >
          {remainingSeconds > 0
            ? `请阅读（${remainingSeconds} 秒）`
            : '我已阅读并确认'}
        </Button>
      </section>
    </div>
  );
}
